import { Link } from 'found';
import { DateTime, Duration } from 'luxon';
import PropTypes from 'prop-types';
import React, {
  useEffect, useMemo, useReducer, useRef, useState,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import { ButtonGroup, ButtonToolbar } from 'reactstrap';

import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import Heading from 'src/enosikit/components/Heading';
import getFullAddressString from 'src/util/address';
import {
  LIST_FILTER_ALL, TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL,
  TRADE_LIST_PAGE_SIZE, TRADE_TYPE_CONTRACTED, TRADE_TYPE_NOMINATED,
  TRADE_TYPE_RESIDUAL,
} from 'src/util/constants';
import BrowserProtocol from 'src/util/history';
import { getTimezone } from 'src/util/time';

import TradeListHeaderPlaceholder from './HeaderPlaceholder';
import TradeListControls from './TradeListControls';
import TradeListTable from './TradeListTable';
import TradeListTablePlaceholder from './TradeListTable/Placeholder';
import useTradeData, { getTradesFromMeters, hasTradeDirectionAndType } from './TradeListTable/hooks/useTradeData';
import useTradePagination from './TradeListTable/hooks/useTradePagination';
import buildLoadingOverlayState from './helpers/buildLoadingOverlayState';
import { TradeListLoadingActions, TradeListLoadingStates } from './reducer/constants';
import { defaultInitialState, loadingStateReducer } from './reducer/tradeListLoadingReducer';

/**
 * GraphQL fragment for fetching property data required by the TradeList component.
 *
 * Retrieves essential property information including identification, active time range,
 * address details, and timezone configuration needed for trade data display and filtering.
 */
const TradeListFragment = graphql`
fragment TradeList on Query
@argumentDefinitions(
  uuid: {type: "ID!"}
) {
  property(uuid:$uuid) {
    uuid
    title
    active { start finish }
    address {
      line1
      line2
      city
      state
      postcode
      gps {
        latitude
        longitude
      }
    }
    timezone
  }
}
`;

/**
 * Renders property settings control button for navigation to property configuration.
 *
 * Creates a button toolbar with a link to the property settings page, allowing users
 * to access property-specific configuration options from the trade list view.
 * @param {object} property - Property object from GraphQL schema (Property type)
 * @param {string} property.uuid - Unique identifier for the property
 * @returns {React.ReactElement} ButtonToolbar containing property settings link
 */
const renderPropertyControls = (property) => (
  <ButtonToolbar>
    <ButtonGroup className="ms-2">
      <Link
        to={`/properties/${property.uuid}/settings`}
        className="btn btn-darken"
      >
        <FormattedMessage id="trade.trade_list.property_settings.label" defaultMessage="Property Settings" />
      </Link>
    </ButtonGroup>
  </ButtonToolbar>
);

/**
 * TradeList component displays a comprehensive view of energy trades for a specific property.
 *
 * This component provides a paginated, filterable interface for viewing trade data including:
 * - Date-based filtering with timezone-aware date selection
 * - Trade direction filtering (buy/sell) and type filtering (contracted/nominated/residual)
 * - Client-side pagination with configurable page sizes
 * - Property validation against active time ranges
 * - Real-time data fetching with progress indicators
 * - URL state management for date parameters
 *
 * The component follows a four-stage data fetching pattern:
 * 1. Fetch property data
 * 2. Fetch property meters
 * 3. Fetch trade rules for each meter
 * 4. Fetch trades for each meter
 * @param {object} props - Component props
 * @param {object} props.match - React Router match object containing location and params
 * @param {object} props.match.location - Location object with pathname for URL management
 * @param {string} props.match.location.pathname - Current pathname for navigation state
 * @param {string} props.queryDate - Initial date for trade data display (from router variables)
 * @returns {React.ReactElement} Complete trade list interface with controls, table, and pagination
 */
function TradeList(props) {
  if (!props) {
    return (
      <>
        <TradeListHeaderPlaceholder />
        <TradeListTablePlaceholder />
      </>
    );
  }

  const { queryDate, match } = props || {};
  const { location } = match || {};
  const { pathname } = location || {};
  const intl = useIntl();
  const data = useFragment(TradeListFragment, props);
  const { property } = data || {};
  const { timezone: propertyTimezone, active: propertyActive } = property || {};
  const propertyActiveStart = propertyActive?.start
    ? DateTime.fromSeconds(propertyActive.start)
    : null;
  const propertyActiveFinish = propertyActive?.finish
    ? DateTime.fromSeconds(propertyActive.finish)
    : null;

  const timezone = getTimezone(propertyTimezone);

  let initialDate = DateTime.local({ zone: timezone })
    .startOf('day')
    .minus(Duration.fromISO('P1D'));

  if (queryDate) {
    const ts = DateTime.fromISO(queryDate, { zone: timezone });
    if (ts.invalidExplanation === null) {
      initialDate = ts;
    }
  }

  // Validate the date, as a timerange, as inclusive with the property active timerange.
  const [isDateOutOfRange, setIsDateOutOfRange] = useState(false);

  // Effect to validate date range when dependencies change
  useEffect(() => {
    const isOutsideRange = (
      (propertyActiveStart && initialDate < propertyActiveStart)
  || (propertyActiveFinish && initialDate.plus({ days: 1 }) > propertyActiveFinish)
    );

    // Only update state if the value actually changes to prevent unnecessary re-renders
    setIsDateOutOfRange(isOutsideRange);
  }, [initialDate, propertyActiveStart, propertyActiveFinish]);

  const defaultFilter = {
    direction: [],
    type: [],
  };

  const [date, setDate] = useState(initialDate);
  const [filter, setFilter] = useState(defaultFilter);

  const [loadingState, loadingStateDispatch] = useReducer(
    loadingStateReducer,
    defaultInitialState(),
  );

  const firstTimeRender = useRef(true);

  /**
   * Memoized time range calculation for trade data fetching.
   *
   * Creates a 24-hour time range starting from the selected date at 00:00:00
   * and ending at 00:00:00 the next day. This range is used for filtering
   * trade data and is recalculated when the date or timezone changes.
   * @type {object}
   * @property {DateTime} start - Start of the time range (selected date at midnight)
   * @property {DateTime} finish - End of the time range (next day at midnight)
   */
  const timeRange = useMemo(() => ({
    start: date,
    finish: date.plus({ days: 1 }),
  }), [date]);

  /**
   * URL state management effect for date parameter synchronization.
   *
   * Updates the browser URL with the current date selection to maintain
   * shareable URLs and browser history. Skips the first render to avoid
   * replacing the initial URL state on component mount.
   *
   * The effect:
   * - Converts the selected date to ISO format for URL encoding
   * - Uses REPLACE action to avoid cluttering browser history
   * - Encodes date parameters properly for URL safety
   * - Maintains current pathname while updating search parameters
   */
  useEffect(
    () => {
      if (!firstTimeRender.current) {
        const dateRange = {
          date: timeRange.start.toISODate(),
        };
        BrowserProtocol.navigate({
          action: 'REPLACE',
          pathname,
          search: `?${Object.keys(dateRange).map((k) => [k, encodeURIComponent(dateRange[k])].join('=')).join('&')}`,
          hash: '',
          state: dateRange,
        });
      }
    },
    [date],
  );

  const { combinedData } = useTradeData({
    property,
    stateDispatchFunc: loadingStateDispatch,
    timeRange,
  });

  /**
   * Memoized trade data processing and filtering.
   *
   * Transforms the raw meter data from GraphQL into a flat array of trade objects,
   * then applies user-selected filters for trade direction (buy/sell) and type
   * (contracted/nominated/residual). The processing includes:
   *
   * 1. Extracting trades from all meters using tradesFromMeters helper
   * 2. Creating filter function based on current filter state
   * 3. Applying filters to return only matching trades
   *
   * Returns empty array if no meter data is available. Recalculates when
   * combinedData or filter state changes.
   * @type {Array<object>} Array of filtered trade objects with linked rule and meter data
   */
  const trades = useMemo(() => {
    if (!combinedData?.property?.meters?.edges) {
      return [];
    }

    const allTrades = getTradesFromMeters(combinedData.property.meters);
    const filterFunc = hasTradeDirectionAndType(filter.direction, filter.type);
    return allTrades.filter((trade) => filterFunc(trade));
  }, [combinedData, filter]);

  /**
   * Client-side pagination hook for trade data display.
   *
   * Manages pagination state and provides paginated trade data for the current page.
   * Automatically resets to page 1 when time range or filter criteria change to
   * ensure users don't end up on empty pages after data changes.
   *
   * Configuration:
   * - Resets pagination when timeRange or filter changes
   * - Provides page navigation handlers and pagination metadata
   * @type {object} Pagination state and handlers from useTradePagination hook
   * @property {Array} paginatedTrades - Trades for the current page
   * @property {Function} handlePageChange - Function to navigate to specific page
   * @property {number} currentPage - Current active page number (1-based)
   * @property {number} totalPages - Total number of pages available
   * @property {number} totalItems - Total number of trades across all pages
   */
  const { paginationInfo, handlePageChange } = useTradePagination({
    items: trades,
    itemsPerPage: TRADE_LIST_PAGE_SIZE,
    timeRange,
    filter,
  });

  const { loadingOverlayProps, isOverlayVisible } = buildLoadingOverlayState({
    intl,
    date,
    loadingState,
  });

  useEffect(() => {
    firstTimeRender.current = false;
  }, []);

  /**
   * Handles filter state updates for trade direction and type filtering.
   *
   * Manages toggle behavior for filter options, supporting both individual filter
   * selection/deselection and "All" filter clearing. Automatically clears filter
   * categories when all options within that category are selected.
   * @param {string} key - Filter category key ('direction' or 'type')
   * @param {string} value - Filter value to toggle or LIST_FILTER_ALL to clear all filters
   * @example
   * // Toggle buy direction filter
   * buildFilterUpdate('direction', TRADE_DIRECTION_BUY);
   * @example
   * // Clear all filters
   * buildFilterUpdate('direction', LIST_FILTER_ALL);
   * @example
   * // Toggle contracted type filter
   * buildFilterUpdate('type', TRADE_TYPE_CONTRACTED);
   */
  const buildFilterUpdate = (key, value) => {
    const { direction, type } = filter;

    const newFilter = { direction, type };

    if (value === LIST_FILTER_ALL) {
      newFilter.direction = [];
      newFilter.type = [];
    }

    const filterSet = new Set(newFilter[key]);
    if (filterSet.has(value)) {
      filterSet.delete(value);
    } else {
      filterSet.add(value);
    }

    newFilter[key] = Array.from(filterSet);

    const allDirections = [TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL];
    const allTypes = [TRADE_TYPE_CONTRACTED, TRADE_TYPE_NOMINATED, TRADE_TYPE_RESIDUAL];

    if (allDirections.every((k) => direction.includes(k))) {
      newFilter.direction = [];
    }
    if (allTypes.every((k) => type.includes(k))) {
      newFilter.type = [];
    }
    setFilter(newFilter);
  };

  const { address, uuid, title } = property || {};
  const addr = getFullAddressString(address);
  const pageTitle = intl.formatMessage({ id: 'trade.trade_list.page.title', defaultMessage: 'Enosi - Property: {title}' }, { title });
  const breadcrumbProperties = intl.formatMessage({ id: 'trade.trade_list.properties.breadcrumb', defaultMessage: 'Properties' });
  const breadcrumbTrades = intl.formatMessage({ id: 'trade.trade_list.trades.breadcrumb', defaultMessage: 'Trades' });
  const tradesTitle = intl.formatMessage({ id: 'trade.trade_list.heading.title', defaultMessage: 'Trades' });

  /**
   * Manages state transitions for the loading overlay after data rendering.
   *
   * This effect handles the final stages of the loading state machine:
   * - RENDERING_DATA → DATA_RENDERED → COMPLETE: Triggered when trades data is computed
   * - COMPLETE → HIDE_OVERLAY: Triggered after a brief delay to show completion message
   *
   * The PREPARING_DATA → RENDERING_DATA transition is handled by the data fetching
   * layer (dataBatching.jsx) when data merging completes.
   */
  useEffect(() => {
    // When we transition to RENDERING_DATA state, the trades have been computed
    // and React is rendering them, so we can immediately transition to DATA_RENDERED
    if (loadingState.state === TradeListLoadingStates.RENDERING_DATA) {
      loadingStateDispatch({ type: TradeListLoadingActions.DATA_RENDERED });
    }

    // When we reach COMPLETE state, wait briefly to show the completion message
    // before hiding the overlay
    if (loadingState.state === TradeListLoadingStates.COMPLETE) {
      const timerId = setTimeout(() => {
        loadingStateDispatch({ type: TradeListLoadingActions.HIDE_OVERLAY });
      }, 1000);

      return () => clearTimeout(timerId);
    }

    return undefined;
  }, [loadingState.state]);

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>
      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbProperties, path: '/properties' },
          { name: title, path: `/properties/${uuid}` },
          { name: breadcrumbTrades },
        ]}
      />
      <Heading
        title={title}
        subtitle={addr}
        context={tradesTitle}
        controls={renderPropertyControls(property)}
      />
      <TradeListControls
        date={date}
        dateUpdateFunc={(selectedDate) => setDate(selectedDate)}
        filter={filter}
        filterFunc={(key, value) => buildFilterUpdate(key, value)}
      />
      <TradeListTable
        date={date}
        dateUpdateFunc={(selectedDate) => setDate(selectedDate)}
        handlePageChange={handlePageChange}
        isDateOutOfRange={isDateOutOfRange}
        isOverlayVisible={isOverlayVisible}
        loadingOverlayProps={loadingOverlayProps}
        paginationInfo={paginationInfo}
        propertyId={property?.uuid}
        timezone={timezone}
        trades={trades}
      />
    </>
  );
}

TradeList.propTypes = {
  match: PropTypes.shape({
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }),
  }).isRequired,
};

export default TradeList;
